import { NavLink, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAcademySettings } from '../../hooks/useAcademySettings';
import { useRef, useEffect } from 'react';
import {
  FaBook,
  FaBookOpen,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaCog,
  FaCreditCard,
  FaGlobe,
  FaGraduationCap,
  FaHome,
  FaIdCard,
  FaMagic,
  FaMoneyBillWave,
  FaStarHalfAlt,
  FaUser,
  FaUserGraduate,
  FaUsers,
} from 'react-icons/fa';

const NavIcon = ({ Icon }) => (
  <span
    className="flex h-5 w-5 shrink-0 items-center justify-center text-[15px]"
    aria-hidden
  >
    <Icon className="h-4 w-4 opacity-90" />
  </span>
);

const Sidebar = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const {
    isSidebarOpen,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
    closeSidebar,
  } = useSidebar();
  const { academy } = useAcademySettings();
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest('button[aria-label="Toggle sidebar"]')
      ) {
        closeSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen, closeSidebar]);

  const getNavSections = (role) => {
    const commonItems = [
      { path: '/dashboard', icon: FaHome, labelKey: 'nav.dashboard' },
      { path: '/profile', icon: FaUser, labelKey: 'nav.profile' },
    ];

    const adminSections = [
      {
        label: 'Workspace',
        items: commonItems,
      },
      {
        label: 'Operations',
        items: [
      { path: '/enrollments', icon: FaClipboardList, labelKey: 'nav.enrollments' },
      { path: '/admin/batches', icon: FaCalendarAlt, label: 'Batch Ledger' },
          { path: '/payments', icon: FaMoneyBillWave, labelKey: 'nav.payments' },
      { path: '/lesson-planner', icon: FaMagic, labelKey: 'nav.lessonPlanner' },
        ],
      },
      {
        label: 'Management',
        items: [
          { path: '/courses/available', icon: FaBook, labelKey: 'nav.courses' },
          { path: '/students', icon: FaUserGraduate, labelKey: 'nav.students' },
      { path: '/instructors', icon: FaChalkboardTeacher, labelKey: 'nav.staff' },
          { path: '/users', icon: FaUsers, labelKey: 'nav.users' },
        ],
      },
      {
        label: 'Configuration',
        items: [
      { path: '/admin/verifications', icon: FaIdCard, labelKey: 'nav.verifications' },
      { path: '/settings', icon: FaCog, labelKey: 'nav.settings' },
        ],
      },
    ];

    const instructorItems = [
      { path: '/courses/available', icon: FaBook, labelKey: 'nav.courses' },
      { path: '/schedule', icon: FaCalendarAlt, labelKey: 'nav.schedule' },
      { path: '/lesson-planner', icon: FaMagic, labelKey: 'nav.lessonPlanner' },
    ];

    const staffItems = [
      { path: '/reception/kiosk', icon: FaClipboardList, label: 'Reception kiosk' },
      { path: '/payments', icon: FaMoneyBillWave, labelKey: 'nav.payments' },
    ];

    const studentItems = [
      { path: '/courses/available', icon: FaGraduationCap, labelKey: 'nav.availableCourses' },
      { path: '/courses/my-courses', icon: FaBookOpen, labelKey: 'nav.myCourses' },
      { path: '/schedule', icon: FaCalendarAlt, labelKey: 'nav.classSchedule' },
      { path: '/assignments', icon: FaClipboardList, labelKey: 'nav.assignments' },
      { path: '/grades', icon: FaStarHalfAlt, labelKey: 'nav.grades' },
      { path: '/payments', icon: FaCreditCard, labelKey: 'nav.payments' },
    ];

    switch (role) {
      case 'admin':
        return adminSections;
      case 'manager':
        return adminSections.filter((section) => section.label !== 'Configuration');
      case 'instructor':
      case 'demonstrator':
        return [{ label: 'Workspace', items: [...commonItems, ...instructorItems] }];
      case 'staff':
        return [{ label: 'Workspace', items: [...commonItems, ...staffItems] }];
      case 'student':
        return [{ label: 'Learning', items: [...commonItems, ...studentItems] }];
      default:
        return [{ label: 'Workspace', items: commonItems }];
    }
  };

  const navSections = getNavSections(currentUser?.role);
  const isStudent = currentUser?.role === 'student';
  const collapsed = isSidebarCollapsed;

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-gray-900/45 backdrop-blur-[2px] transition-opacity md:hidden"
          aria-hidden="true"
        />
      )}

      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-30 flex w-72 max-w-[calc(100vw-3rem)] flex-col px-4 py-5 shadow-[2px_0_16px_rgba(0,0,0,0.18)] transition-[width,transform,padding] duration-200 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          isStudent
            ? 'border-r border-primary-900/10 bg-gradient-to-b from-[#4d183f] to-[#2f1028] text-white'
            : 'border-r border-[#24171f] bg-gradient-to-b from-[#32222d] to-[#181319] text-white'
        } md:relative md:translate-x-0 ${
          collapsed ? 'md:w-[5.25rem] md:px-3' : 'md:w-72'
        }`}
      >
        <button
          type="button"
          onClick={toggleSidebarCollapsed}
          className="absolute right-0 top-1/2 z-40 hidden h-7 w-7 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-white/10 bg-[#d61f74] text-white shadow-md transition hover:bg-[#b91c66] md:inline-flex"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <FaChevronRight className="h-3.5 w-3.5" /> : <FaChevronLeft className="h-3.5 w-3.5" />}
        </button>

        <Link
          to="/dashboard"
          className="mb-5 block transition hover:opacity-95"
          onClick={() => closeSidebar()}
        >
          {(() => {
            const displayName = academy?.name || 'Brissbella Academy';
            const parts = displayName.trim().split(/\s+/);
            const nameLine1 =
              academy?.shortName || (parts.length > 1 ? parts[0] : displayName);
            const nameLine2 = academy?.shortName
              ? displayName.replace(
                  new RegExp(`^${academy.shortName}\\s*`, 'i'),
                  '',
                ) || ''
              : parts.slice(1).join(' ');
            return (
              <div
                className={`flex items-center gap-3 rounded-[24px] border border-white/10 bg-white/6 p-2.5 shadow-sm backdrop-blur-sm ${
                  collapsed
                    ? 'md:justify-center md:border-transparent md:bg-transparent md:p-0 md:shadow-none'
                    : ''
                }`}
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white">
                  <img
                    src={academy?.logoURL || '/images/White-Logo-1.png'}
                    alt={academy?.name || 'Brissbella'}
                    className="h-12 w-12 object-contain"
                    style={{ mixBlendMode: 'multiply' }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/images/White-Logo-1.png';
                    }}
                  />
                </span>
                <div className={`min-w-0 flex-1 leading-tight ${collapsed ? 'md:hidden' : ''}`}>
                  <p className="truncate text-sm font-semibold uppercase tracking-[0.16em] text-white">
                    {nameLine1}
                  </p>
                  {nameLine2 ? (
                    <p className="truncate text-[11px] font-medium uppercase tracking-[0.22em] text-white/70">
                      {nameLine2}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })()}
        </Link>

        <div className="space-y-1">
          <NavPillLink
            to="/"
            icon={FaGlobe}
            label={
              academy?.website
                ? academy.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')
                : 'Brissbella.com'
            }
            onClick={closeSidebar}
            student={isStudent}
            collapsed={collapsed}
          />
        </div>

        <div className="px-2 py-4">
          <div className={`h-px ${isStudent ? 'bg-white/10' : 'bg-white/10'}`} />
        </div>

        <nav
          className="no-scrollbar flex-1 space-y-1 overflow-y-auto pb-6 pt-1"
          aria-label="Workspace"
        >
          {navSections.map((section) => (
            <div key={section.label} className="space-y-1.5">
              <p className={`px-4 pt-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35 ${collapsed ? 'md:hidden' : ''}`}>
                {section.label}
              </p>
              {section.items.map((item) => {
                const Icon = item.icon;
                const label = item.label || t(item.labelKey);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) =>
                      `flex min-h-[48px] items-center gap-3 rounded-2xl px-4 py-3 transition duration-200 ${
                        collapsed ? 'md:justify-center md:px-0' : ''
                      } ${
                        isActive
                          ? 'bg-[#d61f74] text-white shadow-[0_16px_30px_-20px_rgba(214,31,116,0.6)]'
                          : isStudent
                          ? 'text-white/80 hover:bg-white/8 hover:text-white'
                          : 'text-white/75 hover:bg-white/8 hover:text-white'
                      }`
                    }
                    onClick={() => closeSidebar()}
                  >
                    <NavIcon Icon={Icon} />
                    <span className={`text-[15px] font-medium ${collapsed ? 'md:hidden' : ''}`}>
                      {label}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mt-auto space-y-4 border-t border-white/10 pt-4">
          <div className={`flex items-center gap-3 rounded-[24px] bg-white/8 px-3 py-3 ${collapsed ? 'md:justify-center md:px-2' : ''}`}>
            <img
              src={
                currentUser?.photoURL ||
                `https://ui-avatars.com/api/?name=${currentUser?.firstName}+${currentUser?.lastName}&background=DB2777&color=fff`
              }
              alt=""
              className="h-10 w-10 rounded-full"
            />
            <div className={`min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
              <p className="truncate text-sm font-semibold text-white">
                {currentUser?.firstName} {currentUser?.lastName}
              </p>
              <p className="text-xs text-white/65 capitalize">{currentUser?.role}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const NavPillLink = ({ to, icon, label, onClick, student, collapsed }) => {
  const Icon = icon;
  return (
    <Link
      to={to}
      title={collapsed ? label : undefined}
      className={`flex min-h-[48px] items-center gap-3 rounded-2xl px-4 py-3 transition ${
        collapsed ? 'md:justify-center md:px-0' : ''
      } ${
        student
          ? 'text-white/75 hover:bg-white/8 hover:text-white'
          : 'text-white/75 hover:bg-white/8 hover:text-white'
      }`}
      onClick={() => onClick()}
    >
      <NavIcon Icon={Icon} />
      <span className={`text-[15px] font-medium ${collapsed ? 'md:hidden' : ''}`}>{label}</span>
    </Link>
  );
};

export default Sidebar;
